// [guanxing patch] M-SW41：导出成图的页脚（品牌 banner）合成。
//
// 三条链路共用本模块：
//   ① 星图导出 · 图1 西方星座连线  → banners/star1.jpg
//   ② 星图导出 · 图2 中国星官连线  → banners/star2.png
//   ③ 太阳系俯视截图              → banners/star3.png
//
// [guanxing patch] M-SW63：同一次画布合成里顺带盖右上角公历日期（用户需求
// 「三张生成的截图 右上角角落 加公历日期 格式为 1900-2-24 要很小很小 贴边」）。
// 第三个参数 stampMs（Unix 毫秒，调用方从引擎 observer.utc / 太阳系桥 getTime
// 换算，见 assets/image-date.js）非空即盖。放这里而不是另起一次合成，是为了
// 不新增画布与第二次 3200² PNG 编码——iOS 内存余量宝贵（M-SW57/58 教训）。
// 行为收窄一处（有意）：banner 加载失败不再整条退回原图，而是**照样出
// 「截图 + 日期」**——印章与页脚互不牵连，日期不会因为页脚挂了而消失；
// 只有合成整体异常才退回原 dataUrl。
//
// 几何（定案：banner 缩到截图宽度）：
//   · 截图零重采样、保持原样；
//   · banner 按「截图宽度 / banner 原始宽度」等比缩放后紧贴底部（水平铺满）；
//   · 成品 = 截图 + 缩放后的 banner（1600×1600 → 1600×1758~1770）。
//
// 接缝（实测）：banner 底色 ≈ rgb(8,13,22) 偏蓝，星图地景底色近纯黑
// （postProcess 的 sampleLandscapeColor 取四角最小值），紧贴会留一条可辨的
// 细缝。故在交接处上下各 8px 做对称羽化：上侧逐行向 banner 顶色靠拢、下侧
// 逐行向截图底色靠拢，最大混合不透明度 0.5。星图导出方形图的最下 54px 落在
// 「地平线圆外」的纯地景填色区（fov=188° ⇒ R≈745.9px、圆心 800 ⇒ 圆底边
// y≈1546），因此羽化不会碰到任何星星像素。

import { drawDateStamp } from '@/assets/image-date.js'

export const FOOTER_DIR = 'banners/'

// 运行时 URL：与取 solar 页同源同法（子路径部署时 BASE_URL 会带前缀）。
export function footerUrl (name) {
  return process.env.BASE_URL + FOOTER_DIR + name
}

const SEAM_BLEND_PX = 8
const SEAM_MAX_ALPHA = 0.5

function loadImage (src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('图片加载失败: ' + src))
    img.src = src
  })
}

// 单行平均色（羽化取样用）
function avgRowColor (ctx, w, y) {
  const d = ctx.getImageData(0, y, w, 1).data
  let r = 0
  let g = 0
  let b = 0
  for (let i = 0; i < d.length; i += 4) {
    r += d[i]
    g += d[i + 1]
    b += d[i + 2]
  }
  const n = d.length / 4
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)]
}

// 交接处羽化：让「截图最底边」与「banner 最顶边」的颜色互相靠拢。
function blendSeam (ctx, w, y0, totalH) {
  const shot = avgRowColor(ctx, w, Math.max(0, y0 - 2)) // 截图侧底色
  const foot = avgRowColor(ctx, w, Math.min(totalH - 1, y0 + 1)) // banner 侧底色
  for (let i = 0; i < SEAM_BLEND_PX; i++) {
    const a = SEAM_MAX_ALPHA * (i + 1) / SEAM_BLEND_PX
    ctx.fillStyle = 'rgba(' + foot[0] + ',' + foot[1] + ',' + foot[2] + ',' + a + ')'
    ctx.fillRect(0, y0 - SEAM_BLEND_PX + i, w, 1)
  }
  for (let i = 0; i < SEAM_BLEND_PX; i++) {
    const a = SEAM_MAX_ALPHA * (SEAM_BLEND_PX - i) / SEAM_BLEND_PX
    ctx.fillStyle = 'rgba(' + shot[0] + ',' + shot[1] + ',' + shot[2] + ',' + a + ')'
    ctx.fillRect(0, y0 + i, w, 1)
  }
}

// 合成。任何一步失败都返回原 dataUrl：页脚与印章都是装饰件，绝不能把导出搞挂。
// stampMs（M-SW63，可选）= 「这一帧画面是哪一刻」的 Unix 毫秒；非空即盖右上角日期。
export async function appendFooter (dataUrl, bannerUrl, stampMs) {
  try {
    const shot = await loadImage(dataUrl)
    const w = shot.naturalWidth || shot.width
    const sh = shot.naturalHeight || shot.height
    if (!w || !sh) return dataUrl
    // banner 是最容易失败的一环（素材 404 / 解码失败）：失败只出「截图 + 日期」，
    // 不让整条链路退回原图——否则日期也一起丢了。
    const banner = await loadBannerOrNull(bannerUrl)
    const bw = banner ? (banner.naturalWidth || banner.width) : 0
    const bh = banner ? (banner.naturalHeight || banner.height) : 0
    const fh = (bw && bh) ? Math.max(1, Math.round(bh * w / bw)) : 0
    const c = document.createElement('canvas')
    c.width = w
    c.height = sh + fh
    const ctx = c.getContext('2d')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(shot, 0, 0, w, sh)
    if (fh > 0) {
      ctx.drawImage(banner, 0, sh, w, fh)
      blendSeam(ctx, w, sh, c.height)
    }
    drawDateStamp(ctx, w, stampMs)
    return c.toDataURL('image/png')
  } catch (e) {
    console.warn('[M-SW41] 页脚合成失败，退回未加页脚的成图:', e)
    return dataUrl
  }
}

// [guanxing patch] M-SW63：banner 降级为可空装饰件（加载失败返回 null，调用方
// 据此只出截图 + 日期）。定义在 appendFooter 之后，让文件首处 `catch` 仍是
// appendFooter 的整体失败分支（M-SW41 断言按此形态钉「失败退回原图」）。
async function loadBannerOrNull (src) {
  try {
    return await loadImage(src)
  } catch (e) {
    console.warn('[M-SW63] 页脚 banner 加载失败，只出截图 + 日期:', e)
    return null
  }
}
